(ns machtfit.dashboard.stepchall.form.route-editor.path
  (:require [machtfit.core :as machtfit]
            [machtfit.dashboard.stepchall.form.core :as-alias form]
            [machtfit.dashboard.stepchall.form.route-editor.point-weight :as point-weight]
            [machtfit.dashboard.stepchall.form.route-editor.shared :as shared]
            [machtfit.stepchall.catmullrom :as catmullrom]
            [re-frame.core :as rf]))

(def ^:private point-size 13)

(defn remove-stage-point-by-index [db stage-index]
  (let [path (-> db :challenge :theme :path)
        stage-points (point-weight/effective-stage-points {:path path
                                                           :stages (-> db :challenge :config :stages)
                                                           :goal (-> db :challenge :config :goal)})
        stage-point-indices (map first stage-points)
        valid-stage-index? (<= 0
                               stage-index
                               (-> stage-point-indices
                                   count
                                   dec))
        point-index (when valid-stage-index?
                      (nth stage-point-indices
                           stage-index))
        new-path (if point-index
                   (update path point-index dissoc :stage?)
                   path)]
    (-> db
        (assoc-in [:challenge :theme :path] new-path)
        (point-weight/calculate :force? (and point-index
                                             (not= point-index 0)
                                             (not= point-index (dec path)))))))

(defn- select-theme-path-point [db index pos]
  (let [current-pos (get-in db [:challenge :theme :path index])
        dx (- (:x pos) (:x current-pos))
        dy (- (:y pos) (:y current-pos))]
    (assoc-in db [:ui :stepchall :route-edit :selected-path-point] {:index index
                                                                    :dx dx
                                                                    :dy dy})))

(defn- mouse-coords-to-svg-space [event]
  (let [mx (.-clientX event)
        my (.-clientY event)
        svg (js/document.getElementById shared/route-editor-svg-id)
        ctm (.getScreenCTM svg)]
    {:x (-> mx
            (- (.-e ctm))
            (/ (.-a ctm)))
     :y (-> my
            (- (.-f ctm))
            (/ (.-d ctm)))}))

(rf/reg-event-db ::mouse-move
  (fn [db [_ event]]
    (let [{:keys [x y]} (mouse-coords-to-svg-space event)
          {:keys [index dx dy]} (get-in db [:ui :stepchall :route-edit :selected-path-point])]
      (if index
        (-> db
            (update-in [:challenge :theme :path index]
                       assoc
                       :x (- x dx)
                       :y (- y dy))
            point-weight/calculate)
        db))))

(defn- challenge-theme-path-has-removable-points? [path]
  (-> path count (> 2)))

(rf/reg-event-db ::point-remove-clicked
  (fn [db [_ index]]
    (let [was-stage-point? (-> db :challenge :theme :path (get index) :stage?)]
      (-> db
          (update-in [:challenge :theme :path]
                     (fn [path]
                       (if (challenge-theme-path-has-removable-points? path)
                         (-> (concat (take index path)
                                     (drop (inc index) path))
                             vec)
                         path)))
          (point-weight/calculate :force? was-stage-point?)))))

(rf/reg-event-db ::point-double-clicked
  (fn [db [_ index]]
    (-> db
        (update-in [:challenge :theme :path index :stage?] not)
        (point-weight/calculate :force? true))))

(rf/reg-sub ::theme-path-point
  :<- [:get [:challenge :theme :path]]

  (fn [path [_ index]]
    (get path index)))

(defn on-mouse-move [event]
  (machtfit/stop-event event)
  (rf/dispatch [::mouse-move event]))

(rf/reg-event-db ::mouse-up
  (fn [db [_ _event]]
    (select-theme-path-point db nil nil)))

(defn on-mouse-up [event]
  (machtfit/stop-event event)
  (rf/dispatch [::mouse-up event]))

(rf/reg-event-db ::point-mouse-down
  (fn [db [_ index event]]
    (select-theme-path-point db index (mouse-coords-to-svg-space event))))

(defn- path-point [index]
  (let [{:keys [x y stage?]} @(rf/subscribe [::theme-path-point index])
        edit-mode @(rf/subscribe [::form/effective-edit-mode])
        edit-mode? (= edit-mode :edit)
        add-or-remove-mode? (= edit-mode :add-or-remove)]
    [:g {:transform (str "translate(" x "," y ")")
         :on-mouse-down (when edit-mode?
                          (comp #(rf/dispatch [::point-mouse-down index %])
                                machtfit/stop-event))

         :on-click (when add-or-remove-mode?
                     (comp #(rf/dispatch [::point-remove-clicked index])
                           machtfit/stop-event))

         :on-double-click (when edit-mode?
                            (comp #(rf/dispatch [::point-double-clicked index])
                                  machtfit/stop-event))

         :style {:cursor (if edit-mode?
                           "grab"
                           "pointer")}}
     [:circle {:style {:fill (if stage?
                               "#f00"
                               "#fff")
                       :stroke "#000"}
               :r point-size}]
     (when add-or-remove-mode?
       (let [width (* point-size 1.1)
             height (* point-size 0.2)]
         [:rect {:x (- (/ width 2))
                 :y (- (/ height 2))
                 :width width
                 :height height
                 :style {:fill "#000"}}]))]))

(rf/reg-sub ::theme-path-point-count
  :<- [:get [:challenge :theme :path]]

  (fn [path _]
    (count path)))

(rf/reg-event-db ::add-theme-path-point
  (fn [db [_ index new-point]]
    (-> db
        (update-in [:challenge :theme :path]
                   (fn [points]
                     (vec (concat (subvec points 0 (inc index))
                                  [new-point]
                                  (subvec points (inc index))))))
        point-weight/calculate)))

(defn- point-to-add [index {:keys [x y] :as point}]
  (let [width (* point-size 1.1)
        height (* point-size 0.2)]
    [:g
     {:transform (str "translate(" x "," y ")")
      :on-click #(rf/dispatch [::add-theme-path-point index point])
      :style {:cursor "pointer"
              :opacity 0.5}}
     [:circle {:style {:fill "#fff"
                       :stroke "#000"}
               :r point-size}]
     [:g
      [:rect {:x (/ width -2)
              :y (/ height -2)
              :width width
              :height height
              :style {:fill "#000"}}]
      [:rect {:x (/ height -2)
              :y (/ width -2)
              :width height
              :height width
              :style {:fill "#000"}}]]]))

(rf/reg-sub ::addable-theme-path-points
  :<- [:get [:challenge :theme :path]]

  (fn [path _]
    (-> path
        catmullrom/catmullrom
        (->> (map-indexed (fn [index leg]
                            [index (catmullrom/interpolate-point-cubic leg 0.5)]))))))

(defn theme-path-points []
  (when-let [edit-mode @(rf/subscribe [::form/effective-edit-mode])]
    [:g
     (into [:g]
           (map (fn [index] [path-point index]))
           (range @(rf/subscribe [::theme-path-point-count])))

     (when (= edit-mode :add-or-remove)
       (into [:g]
             (map (fn [[index point]] [point-to-add index point]))
             @(rf/subscribe [::addable-theme-path-points])))]))
